"use client";

import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { LuArrowRight, LuCheck, LuPackage } from "react-icons/lu";
import NextLink from "next/link";

import { InteractiveButton } from "@components/common/InteractiveButton";
import { Reveal } from "@components/common/Reveal";
import {
  BILLING_RULES,
  PAYMENT_METHODS,
  STATUS_DESCRIPTIONS,
  STEP_EXTRAS,
  WHATSAPP_URL,
} from "@features/home/howItWorksGuide";
import { HOW_IT_WORKS_STEPS } from "@features/home/howItWorksSteps";
import { SiteFooter } from "@features/home/SiteFooter";
import { SiteTopBar } from "@features/home/SiteTopBar";
import {
  MINIMUM_BILLABLE_CBM,
  RATE_CATEGORIES,
} from "@features/services/shipping/rates";
import {
  PACKAGE_TIMELINE,
  SHIPMENT_PRE_RECEIPT_TIMELINE,
} from "@features/services/shipping/shipmentEvents";

// Anchored sections must clear the sticky SiteTopBar when jumped to.
const ANCHOR_OFFSET = "96px";

const GUIDE_SECTIONS = [
  { id: "journey", label: "The journey" },
  { id: "rates", label: "Rates & billing" },
  { id: "statuses", label: "Tracking statuses" },
  { id: "payments", label: "Paying your bill" },
] as const;

const AnchorNav = () => (
  <Flex gap={{ base: 3, md: 5 }} flexWrap="wrap">
    {GUIDE_SECTIONS.map((section, i) => (
      <Link
        key={section.id}
        href={`#${section.id}`}
        display="flex"
        alignItems="center"
        gap={2}
        color="text.secondary"
        _hover={{ color: "fg", textDecoration: "none" }}
        fontSize="sm"
      >
        <Text as="span" fontFamily="mono" fontSize="xs" fontWeight="bold">
          {String(i + 1).padStart(2, "0")}
        </Text>
        {section.label}
      </Link>
    ))}
  </Flex>
);

const SectionHeading = ({
  pill,
  title,
  lead,
}: {
  pill: string;
  title: string;
  lead?: string;
}) => (
  <Box mb={{ base: 8, md: 10 }}>
    <Text
      fontSize="xs"
      fontWeight="semibold"
      color="text.secondary"
      bg="sidebar.hover"
      px={3}
      py={1.5}
      borderRadius="full"
      mb={5}
      w="fit-content"
    >
      {pill}
    </Text>
    <Heading size={{ base: "xl", md: "2xl" }} color="fg" mb={lead != null ? 3 : 0}>
      {title}
    </Heading>
    {lead != null && (
      <Text fontSize="md" color="text.secondary" lineHeight="tall" maxW="2xl">
        {lead}
      </Text>
    )}
  </Box>
);

const StepExtrasBlock = ({ stepId }: { stepId: string }) => {
  const extras = STEP_EXTRAS[stepId as keyof typeof STEP_EXTRAS];
  if (extras == null) {
    return null;
  }
  return (
    <Stack gap={4} mt={2}>
      {extras.checklist != null && (
        <Box
          borderWidth="1px"
          borderColor="border.light"
          borderRadius="lg"
          bg="sidebar.background"
          p={5}
        >
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="text.secondary"
            fontFamily="mono"
            textTransform="uppercase"
            letterSpacing=".08em"
            mb={4}
          >
            {extras.checklist.title}
          </Text>
          <Stack gap={3}>
            {extras.checklist.items.map((item) => (
              <Flex key={item.label} gap={3} align="flex-start">
                <Box
                  as={LuCheck}
                  boxSize={4}
                  color={item.optional === true ? "text.secondary" : "fg"}
                  mt="3px"
                  flexShrink={0}
                  aria-hidden
                />
                <Box>
                  <Flex align="center" gap={2} flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="medium" color="fg">
                      {item.label}
                    </Text>
                    {item.optional === true && (
                      <Text
                        as="span"
                        fontSize="2xs"
                        fontFamily="mono"
                        color="text.secondary"
                        borderWidth="1px"
                        borderColor="border.light"
                        borderRadius="sm"
                        px={1.5}
                        py={0.5}
                      >
                        OPTIONAL
                      </Text>
                    )}
                  </Flex>
                  {item.hint != null && (
                    <Text fontSize="xs" color="text.secondary" mt={0.5}>
                      {item.hint}
                    </Text>
                  )}
                </Box>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}
      {extras.note != null && (
        <Text fontSize="md" color="text.secondary" lineHeight="tall" maxW="2xl">
          {extras.note}
        </Text>
      )}
      {extras.links != null && (
        <Flex gap={4} flexWrap="wrap">
          {extras.links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              {...(link.href.startsWith("http")
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              display="inline-flex"
              alignItems="center"
              gap={1.5}
              fontSize="sm"
              fontWeight="medium"
              color="fg"
              textDecoration="underline"
              textUnderlineOffset="4px"
              _hover={{ color: "text.secondary" }}
            >
              {link.label}
              <LuArrowRight size={14} aria-hidden />
            </Link>
          ))}
        </Flex>
      )}
    </Stack>
  );
};

const StepRow = ({
  step,
  index,
}: {
  step: (typeof HOW_IT_WORKS_STEPS)[number];
  index: number;
}) => {
  const Icon = step.icon;
  const isLast = index === HOW_IT_WORKS_STEPS.length - 1;
  return (
    <Flex gap={6}>
      <Flex direction="column" align="center" flexShrink={0}>
        <Flex
          boxSize="56px"
          borderRadius="full"
          borderWidth="1px"
          borderColor="border.light"
          bg="sidebar.background"
          align="center"
          justify="center"
        >
          <Box as={Icon} boxSize={6} color="fg" aria-hidden />
        </Flex>
        {!isLast && (
          <Box flex={1} w="1px" bg="border.light" my={2} minH="40px" />
        )}
      </Flex>
      <Flex direction="column" gap={2} pb={{ base: 10, md: 14 }} flex={1}>
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="text.secondary"
          fontFamily="mono"
        >
          STEP {String(index + 1).padStart(2, "0")}
        </Text>
        <Heading size="lg" color="fg">
          {step.title}
        </Heading>
        <Text fontSize="md" color="text.secondary" lineHeight="tall" maxW="2xl">
          {step.detail}
        </Text>
        <StepExtrasBlock stepId={step.id} />
      </Flex>
    </Flex>
  );
};

const RatesSection = () => (
  <Box
    as="section"
    id="rates"
    scrollMarginTop={ANCHOR_OFFSET}
    px={{ base: 6, md: 12, lg: 16 }}
    py={{ base: 16, md: 20 }}
    bg="sidebar.background"
  >
    <Box maxW="3xl" mx="auto">
      <Reveal>
        <SectionHeading
          pill="Rates & billing"
          title="What shipping costs"
          lead="Freight is priced per CBM (cubic metre), and the rate depends on what your goods are. We classify them automatically from your description."
        />
      </Reveal>

      {/* Desktop: table */}
      <Reveal hideBelow="md">
        <Table.Root size="md" variant="outline" borderColor="border.light">
          <Table.Header>
            <Table.Row bg="surface.background">
              <Table.ColumnHeader color="text.secondary">
                Category
              </Table.ColumnHeader>
              <Table.ColumnHeader color="text.secondary" textAlign="end">
                Rate per CBM
              </Table.ColumnHeader>
              <Table.ColumnHeader color="text.secondary">
                Typical goods
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {RATE_CATEGORIES.map((category) => (
              <Table.Row key={category.key} bg="transparent">
                <Table.Cell fontWeight="semibold" color="fg">
                  {category.label}
                </Table.Cell>
                <Table.Cell textAlign="end" fontFamily="mono" color="fg">
                  ${category.ratePerCbm} {category.currency}
                </Table.Cell>
                <Table.Cell color="text.secondary" fontSize="sm">
                  {category.summary}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Reveal>

      {/* Mobile: stacked cards, price prominent */}
      <Box hideFrom="md">
        <Stack gap={3}>
          {RATE_CATEGORIES.map((category, i) => (
            <Reveal key={category.key} delay={i * 60}>
              <Box
                borderWidth="1px"
                borderColor="border.light"
                borderRadius="lg"
                bg="surface.background"
                p={4}
              >
                <Flex justify="space-between" align="baseline" gap={3}>
                  <Text fontSize="sm" fontWeight="semibold" color="fg">
                    {category.label}
                  </Text>
                  <Text fontFamily="mono" fontSize="lg" color="fg">
                    ${category.ratePerCbm}
                    <Text as="span" fontSize="xs" color="text.secondary">
                      /CBM
                    </Text>
                  </Text>
                </Flex>
                <Text fontSize="xs" color="text.secondary" mt={1}>
                  {category.summary}
                </Text>
              </Box>
            </Reveal>
          ))}
        </Stack>
      </Box>

      <Reveal delay={120}>
        <Box mt={{ base: 8, md: 10 }}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="text.secondary"
            fontFamily="mono"
            textTransform="uppercase"
            letterSpacing=".08em"
            mb={4}
          >
            How billing works
          </Text>
          <Stack gap={3}>
            {BILLING_RULES.map((rule) => (
              <Flex key={rule} gap={3} align="flex-start">
                <Box
                  boxSize={1.5}
                  borderRadius="full"
                  bg="text.secondary"
                  mt={2}
                  flexShrink={0}
                />
                <Text fontSize="sm" color="text.secondary" lineHeight="tall">
                  {rule}
                </Text>
              </Flex>
            ))}
          </Stack>
          <Text fontSize="xs" color="text.secondary" mt={5}>
            All rates in USD per CBM with a {MINIMUM_BILLABLE_CBM} CBM minimum.
            Your tracking page shows the Ghana cedi equivalent.
          </Text>
        </Box>
      </Reveal>
    </Box>
  </Box>
);

const StatusList = ({
  title,
  entries,
}: {
  title: string;
  entries: readonly { id: keyof typeof STATUS_DESCRIPTIONS; label: string }[];
}) => (
  <Box>
    <Text
      fontSize="xs"
      fontWeight="bold"
      color="text.secondary"
      fontFamily="mono"
      textTransform="uppercase"
      letterSpacing=".08em"
      mb={5}
    >
      {title}
    </Text>
    <Stack gap={0}>
      {entries.map((entry, i) => (
        <Flex key={entry.id} gap={4}>
          <Flex direction="column" align="center" flexShrink={0} pt="6px">
            <Box boxSize={2.5} borderRadius="full" bg="fg" flexShrink={0} />
            {i < entries.length - 1 && (
              <Box flex={1} w="1px" bg="border.light" my={1} minH="24px" />
            )}
          </Flex>
          <Box pb={i < entries.length - 1 ? 5 : 0}>
            <Text fontSize="sm" fontWeight="semibold" color="fg">
              {entry.label}
            </Text>
            <Text fontSize="sm" color="text.secondary" lineHeight="tall">
              {STATUS_DESCRIPTIONS[entry.id]}
            </Text>
          </Box>
        </Flex>
      ))}
    </Stack>
  </Box>
);

const StatusGlossarySection = () => (
  <Box
    as="section"
    id="statuses"
    scrollMarginTop={ANCHOR_OFFSET}
    px={{ base: 6, md: 12, lg: 16 }}
    py={{ base: 16, md: 20 }}
  >
    <Box maxW="3xl" mx="auto">
      <Reveal>
        <SectionHeading
          pill="Tracking statuses"
          title="What every status means"
          lead="Your tracking page shows one of these statuses at every point in the journey. Here's how to read them."
        />
      </Reveal>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 10, md: 8 }}>
        <Reveal>
          <StatusList
            title="Before we receive your goods"
            entries={SHIPMENT_PRE_RECEIPT_TIMELINE}
          />
        </Reveal>
        <Reveal delay={80}>
          <StatusList
            title="Once your package is with us"
            entries={PACKAGE_TIMELINE}
          />
        </Reveal>
      </SimpleGrid>
    </Box>
  </Box>
);

const PaymentsSection = () => (
  <Box
    as="section"
    id="payments"
    scrollMarginTop={ANCHOR_OFFSET}
    px={{ base: 6, md: 12, lg: 16 }}
    py={{ base: 16, md: 20 }}
    bg="sidebar.background"
  >
    <Box maxW="3xl" mx="auto">
      <Reveal>
        <SectionHeading
          pill="Payments"
          title="Paying your bill"
          lead="Your bill lives on your tracking page — in USD with the cedi equivalent — and updates from unpaid to partially paid to paid as payments land."
        />
      </Reveal>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        {PAYMENT_METHODS.map((method, i) => (
          <Reveal key={method.title} delay={i * 80}>
            <Box
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="lg"
              bg="surface.background"
              p={6}
              h="100%"
            >
              <Heading size="sm" color="fg" mb={2}>
                {method.title}
              </Heading>
              <Text fontSize="sm" color="text.secondary" lineHeight="tall">
                {method.description}
              </Text>
            </Box>
          </Reveal>
        ))}
      </SimpleGrid>
    </Box>
  </Box>
);

const FaqLinkBand = () => (
  <Box as="section" px={{ base: 6, md: 12, lg: 16 }} py={{ base: 12, md: 16 }}>
    <Flex
      maxW="3xl"
      mx="auto"
      direction={{ base: "column", sm: "row" }}
      align={{ base: "flex-start", sm: "center" }}
      justify="space-between"
      gap={4}
    >
      <Box>
        <Heading size="md" color="fg" mb={1}>
          More questions?
        </Heading>
        <Text fontSize="sm" color="text.secondary">
          Check the FAQ, or message us directly — we reply fast.
        </Text>
      </Box>
      <Flex gap={3} flexWrap="wrap">
        <NextLink href="/#faq">
          <Button size="sm" variant="surfaceOutline">
            Read the FAQ
          </Button>
        </NextLink>
        <Link
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          _hover={{ textDecoration: "none" }}
        >
          <Button size="sm" variant="surfaceOutline">
            WhatsApp us
          </Button>
        </Link>
      </Flex>
    </Flex>
  </Box>
);

export const HowItWorksPage = () => (
  <Box
    position="relative"
    minH="100dvh"
    bg="surface.background"
    css={{ scrollBehavior: "smooth" }}
  >
    <SiteTopBar />

    <Box as="section" px={{ base: 6, md: 12, lg: 16 }} py={{ base: 12, md: 16 }}>
      <Box maxW="3xl" mx="auto">
        <Reveal>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="text.secondary"
            bg="sidebar.hover"
            px={3}
            py={1.5}
            borderRadius="full"
            mb={5}
            w="fit-content"
          >
            How it works
          </Text>
          <Heading
            size={{ base: "2xl", md: "3xl" }}
            color="fg"
            mb={4}
            lineHeight="1.15"
          >
            From a rate quote to your door
          </Heading>
          <Text fontSize="lg" color="text.secondary" lineHeight="tall" mb={6}>
            The complete guide to shipping with Elk Cargo — the six-step
            journey from Guangzhou to Ghana, what it costs, what every
            tracking status means, and how to pay your bill.
          </Text>
          <Flex direction="column" gap={6}>
            <AnchorNav />
            <NextLink href="/services">
              <InteractiveButton size="sm">
                <Flex as="span" align="center" gap={1.5}>
                  Start shipping <LuPackage />
                </Flex>
              </InteractiveButton>
            </NextLink>
          </Flex>
        </Reveal>
      </Box>
    </Box>

    <Box
      as="section"
      id="journey"
      scrollMarginTop={ANCHOR_OFFSET}
      px={{ base: 6, md: 12, lg: 16 }}
      pb={{ base: 16, md: 20 }}
    >
      <Box maxW="3xl" mx="auto">
        {HOW_IT_WORKS_STEPS.map((step, i) => (
          <Reveal key={step.id} delay={i * 60}>
            <StepRow step={step} index={i} />
          </Reveal>
        ))}
      </Box>
    </Box>

    <RatesSection />
    <StatusGlossarySection />
    <PaymentsSection />
    <FaqLinkBand />

    <Box
      as="section"
      px={{ base: 6, md: 12, lg: 16 }}
      py={{ base: 16, md: 20 }}
      bg="sidebar.background"
    >
      <Flex
        direction="column"
        align="center"
        textAlign="center"
        gap={5}
        maxW="2xl"
        mx="auto"
      >
        <Heading size="xl" color="fg">
          Ready to ship?
        </Heading>
        <Text fontSize="md" color="text.secondary" lineHeight="tall">
          Describe your goods and get your rate in minutes.
        </Text>
        <Flex gap={3} flexWrap="wrap" justify="center">
          <NextLink href="/services">
            <InteractiveButton size="sm">Start shipping</InteractiveButton>
          </NextLink>
          <NextLink href="/track">
            <Button size="sm" variant="surfaceOutline">
              Track a shipment
            </Button>
          </NextLink>
        </Flex>
      </Flex>
    </Box>

    <SiteFooter />
  </Box>
);
